import { MetricCard } from '@components/cards/MetricCard';
import { RewardsChart } from '@components/rewards/RewardsChart';
import { EcoButton, EcoCard, EcoImage, EcoText, EcoTooltip } from '@components/shared';
import { EcoButtonProps } from '@components/shared/EcoButton';
import { EcoPageTitle } from '@components/shared/EcoPageTitle';
import EcoScrollView from '@components/views/EcoScrollView';
import { formatNumber } from '@ecocart/universal-utils';
import { useFeaturePermission } from '@hooks/useFeaturePermission';
import { useRewards } from '@hooks/useRewards';
import { useRewardsModal } from '@hooks/useRewardsModal';
import { PUBLIC_IMG_URL } from '@utils/constants/config';
import { Gap } from '@utils/layout';
import { format } from 'date-fns';
import React from 'react';
import { View } from 'react-native';
import { GlobalScreenProps } from '../navigation/types';

export const REWARD_DESCRIPTIONS = {
  tapcart: 'Build stronger customer relationships with mobile apps',
  klaviyo: 'Marketing Automation for Email Marketing, SMS & CDP',
  marketing_spend: 'Leverage your EcoCart rewards to cover ad spend or agency fees',
  giveaways: 'Team up with EcoCart to create a giveaway champaign to drive sales',
  treet: 'Most flexible branded resale solution for modern brands',
  maergo: 'Maergo provides affordable & low-carbon nationwide delivery',
  buy_from_ecocart: "You can purchase certified carbon credits from Ecocart's portfolio of vetted offsetting projects",
  life_cycle_analysis: 'The Ecocart Plus subscription supports multiple Life Cycle Analysis of your products'
};

export default function RewardsScreen({}: GlobalScreenProps): JSX.Element {
  const { isFeaturePermissioned } = useFeaturePermission('msx_dev');
  const { data: rewards, currentMonthValue, redeemableAmount } = useRewards();
  const { showRewardsFormModal } = useRewardsModal();

  if (!isFeaturePermissioned) return <></>;

  const marketCardConfig: CardProps[] = [
    {
      title: 'Tapcart',
      description: REWARD_DESCRIPTIONS.tapcart,
      logo: <EcoImage width={120} height={28} src={PUBLIC_IMG_URL + '/tapcart.svg'} />,
      ctaButtonProps: { isDisabled: !redeemableAmount, onPress: () => showRewardsFormModal('tapcart') }
    },
    {
      title: 'Klaviyo',
      description: REWARD_DESCRIPTIONS.klaviyo,
      logo: <EcoImage width={91} height={28} src={PUBLIC_IMG_URL + '/klaviyo.svg'} />,
      ctaButtonProps: { isDisabled: !redeemableAmount, onPress: () => showRewardsFormModal('klaviyo') }
    },
    {
      title: 'Cover your marketing spend',
      description: REWARD_DESCRIPTIONS.marketing_spend,
      ctaButtonProps: { isDisabled: !redeemableAmount, onPress: () => showRewardsFormModal('marketing_spend') }
    },
    {
      title: 'Giveaways',
      description: REWARD_DESCRIPTIONS.giveaways,
      ctaButtonProps: { isDisabled: !redeemableAmount, onPress: () => showRewardsFormModal('giveaways') }
    }
  ];

  const sustainabilityCardConfig: CardProps[] = [
    {
      title: 'Treet',
      description: REWARD_DESCRIPTIONS.treet,
      logo: <EcoImage width={88} height={24} src={PUBLIC_IMG_URL + '/treet.png'} />,
      ctaButtonProps: { isDisabled: !redeemableAmount, onPress: () => showRewardsFormModal('treet') }
    },
    {
      title: 'Maergo',
      description: REWARD_DESCRIPTIONS.maergo,
      logo: <EcoImage width={103} height={24} src={PUBLIC_IMG_URL + '/maergo.svg'} />,
      ctaButtonProps: { isDisabled: !redeemableAmount, onPress: () => showRewardsFormModal('maergo') }
    },
    {
      title: 'Buy carbon offsets from us',
      description: REWARD_DESCRIPTIONS.buy_from_ecocart,
      ctaButtonProps: { isDisabled: !redeemableAmount, onPress: () => showRewardsFormModal('buy_from_ecocart') }
    },
    {
      title: 'Life Cycle analysis',
      description: REWARD_DESCRIPTIONS.life_cycle_analysis,
      ctaButtonProps: { isDisabled: !redeemableAmount, onPress: () => showRewardsFormModal('life_cycle_analysis') }
    }
  ];

  return (
    <EcoScrollView>
      <EcoPageTitle className="mb-5" title="Rewards" subTitle="Earn cash when your customers offset their purchases" />
      <View style={Gap(4)}>
        <View className="flex-row flex-wrap" style={Gap(4)}>
          <MetricCard
            title="Available balance"
            value={rewards ? formatNumber(redeemableAmount, { style: 'currency', currency: 'USD' }) : undefined}
            trend={0}
            infoText="Available balance doesn't reflect rewards earned this month"
          />
          <MetricCard
            title={'Amount earned this month (' + format(new Date(), 'MMM yyyy') + ')'}
            value={currentMonthValue ? formatNumber(currentMonthValue, { style: 'currency', currency: 'USD' }) : undefined}
            trend={0}
            infoText="Rewards earned this month will be available for redemption in the next month"
          />
          <MetricCard
            title="Total amount earned all time"
            value={rewards?.total_rewards_usd ? formatNumber(rewards.total_rewards_usd, { style: 'currency', currency: 'USD' }) : undefined}
            trend={0}
          />
        </View>
        <RewardsChart />
        <View style={Gap(3)} className="mt-5">
          <EcoText fontSize="xl" fontWeight="medium">
            Redeem your rewards
          </EcoText>
          <View style={Gap()}>
            <EcoText fontSize="lg" color="subdued">
              Marketing
            </EcoText>
            <View className="flex-row flex-wrap" style={Gap(4)}>
              {marketCardConfig.map((config) => (
                <RewardCard key={config.title} {...config} />
              ))}
            </View>
          </View>

          <View style={Gap()} className="mt-4">
            <EcoText fontSize="lg" color="subdued">
              Sustainability
            </EcoText>
            <View className="flex-row flex-wrap" style={Gap(4)}>
              {sustainabilityCardConfig.map((config) => (
                <RewardCard key={config.title} {...config} />
              ))}
            </View>
          </View>
        </View>
      </View>
    </EcoScrollView>
  );
}

interface CardProps {
  title: string;
  description: string;
  logo?: JSX.Element;
  ctaButtonProps: EcoButtonProps;
}

const RewardCard = (props: CardProps): JSX.Element => {
  const { title, description, logo, ctaButtonProps } = props;
  return (
    <EcoCard
      className="p-4 justify-between rounded-sm border border-gray-200 flex-1 basis-full sm:basis-2/5 lg:basis-[200px]"
      style={Gap(6)}
    >
      <View style={Gap(6)}>
        {logo}
        <View style={Gap(2)}>
          <EcoText fontSize="lg" fontWeight="medium">
            {title}
          </EcoText>
          <EcoText color="subdued" fontSize="sm">
            {description}
          </EcoText>
        </View>
      </View>
      <EcoTooltip
        tooltip={
          <View style={Gap(2)} className="w-[300px]">
            <EcoText fontSize="lg" fontWeight="medium" color="enabled-inverted">
              Insufficient Balance
            </EcoText>
            <EcoText fontSize="sm" color="enabled-inverted">
              You do not have enough balance to redeem rewards
            </EcoText>
          </View>
        }
        placement="top"
      >
        <EcoButton {...ctaButtonProps} variant="outlined" fullWidth size="sm">
          Redeem
        </EcoButton>
      </EcoTooltip>
    </EcoCard>
  );
};
